/**
 * Format a patient's explicit "normal range" band and grade a value against
 * it. Rule resolution (sex, age at the study date, the age fallback) lives in
 * `referenceRangeRules`; the guideline provenance in `rangeSource`.
 *
 * Mirrors `codings/reference_ranges.py` on the backend so the PDF and the
 * report-detail page render identical strings. Keep the two in sync.
 *
 * Each band set stores severities (`normal`, `borderline`, `mild`,
 * `moderate`, `severe`) as `{operator, value}`. This module reads the
 * `normal` entry directly:
 *
 *    {operator: 'between', value: [4.2, 5.8]}  ->  "4.2 - 5.8"
 *    {operator: 'lt',      value: 5.9}         ->  "< 5.9"
 *    {operator: 'lte',     value: 14}          ->  "≤ 14"
 *    {operator: 'gt',      value: 51}          ->  "> 51"
 *    {operator: 'gte',     value: 60}          ->  "≥ 60"
 *
 * Values are rendered EXACTLY as authored in the xlsx — no precision
 * rounding (so `2.16` stays `2.16`, not `2.2`).
 *
 * Returns null when no `normal` entry exists for the patient.
 */

import {
  resolveSeverityRanges,
  type ReferenceRanges,
  type SeverityBand,
  type SeverityRanges,
} from './referenceRangeRules';

export { resolveSeverityRanges };
export type { ReferenceRanges };

const OPERATOR_PREFIX: Record<string, string> = {
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
};

function formatThreshold(value: number): string {
  // String(number) preserves what the JSON had — 2.16 stays "2.16",
  // 14 stays "14". No rounding.
  return String(value);
}

export function formatBand(
  band: SeverityBand | null | undefined
): string | null {
  if (!band || typeof band !== 'object') return null;
  const { operator, value } = band;
  if (value === undefined || value === null) return null;

  if (operator === 'between') {
    if (!Array.isArray(value) || value.length !== 2) return null;
    const [v0, v1] = value;
    // referenceRanges flows in as `unknown` and is cast at the call site, so
    // runtime values may not respect SeverityBand — guard against non-number
    // and NaN to avoid rendering "NaN" / "foo - bar" on clinical data.
    if (typeof v0 !== 'number' || typeof v1 !== 'number') return null;
    if (Number.isNaN(v0) || Number.isNaN(v1)) return null;
    const low = formatThreshold(v0);
    const high = formatThreshold(v1);
    // A degenerate [x, x] band shouldn't render as "0 - 0".
    if (low === high) return low;
    return `${low} - ${high}`;
  }

  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  const prefix = OPERATOR_PREFIX[operator ?? ''];
  if (!prefix) return null;
  return `${prefix} ${formatThreshold(value)}`;
}

export function deriveNormalRangeForSex(
  severityRanges: SeverityRanges | null | undefined
): string | null {
  if (!severityRanges || typeof severityRanges !== 'object') return null;
  return formatBand(severityRanges.normal);
}

export function formatNormalRange(
  referenceRanges: ReferenceRanges | null | undefined,
  sex: string | null | undefined,
  age?: number | null
): string | null {
  return deriveNormalRangeForSex(
    resolveSeverityRanges(referenceRanges, sex, age)
  );
}

// Evaluate the band numerically — mirrors formatBand's operator handling but
// compares instead of formatting, so single-sided bands (lt/lte/gt/gte) are
// flagged too, not just `between`.
function isValueOutsideBand(
  band: SeverityBand | null | undefined,
  value: number
): boolean {
  if (!band || typeof band !== 'object') return false;
  const { operator, value: bound } = band;
  if (bound === undefined || bound === null) return false;

  if (operator === 'between') {
    if (!Array.isArray(bound) || bound.length !== 2) return false;
    const [low, high] = bound;
    if (typeof low !== 'number' || typeof high !== 'number') return false;
    if (Number.isNaN(low) || Number.isNaN(high)) return false;
    return value < low || value > high;
  }

  if (typeof bound !== 'number' || Number.isNaN(bound)) return false;
  switch (operator) {
    case 'lt':
      return value >= bound;
    case 'lte':
      return value > bound;
    case 'gt':
      return value <= bound;
    case 'gte':
      return value < bound;
    default:
      return false;
  }
}

function isOutsideNormalBand(
  referenceRanges: ReferenceRanges | null | undefined,
  sex: string | null | undefined,
  value: number,
  age?: number | null
): boolean {
  const severityRanges = resolveSeverityRanges(referenceRanges, sex, age);
  if (!severityRanges || typeof severityRanges !== 'object') return false;
  return isValueOutsideBand(severityRanges.normal, value);
}

/**
 * Whether `value` should be flagged as out of normal range. Flags NaN/zero
 * values, and values outside the patient's band — or, when the patient's
 * band is unknown, outside both the male and female bands. False when there
 * is no value or no normal range to compare against.
 */
export function isValueOutOfNormalRange(
  referenceRanges: ReferenceRanges | null | undefined,
  sex: string | null | undefined,
  value: number | null | undefined,
  age?: number | null
): boolean {
  if (value === null || value === undefined) return false;
  if (Number.isNaN(value) || value === 0) return true;

  if (
    resolveSeverityRanges(referenceRanges, sex, age) &&
    isOutsideNormalBand(referenceRanges, sex, value, age)
  )
    return true;

  return (
    isOutsideNormalBand(referenceRanges, 'male', value, age) &&
    isOutsideNormalBand(referenceRanges, 'female', value, age)
  );
}

export type RangeDirection = 'high' | 'low';

// Which side of the normal band `value` falls on. Mirrors isValueOutsideBand,
// but reports the direction instead of a bare boolean:
//   between [lo, hi] → below lo = 'low', above hi = 'high'
//   lt/lte v (normal is below v) → at/above v = 'high'
//   gt/gte v (normal is above v) → at/below v = 'low'
function bandDirection(
  band: SeverityBand | null | undefined,
  value: number
): RangeDirection | null {
  if (!band || typeof band !== 'object') return null;
  const { operator, value: bound } = band;
  if (bound === undefined || bound === null) return null;

  if (operator === 'between') {
    if (!Array.isArray(bound) || bound.length !== 2) return null;
    const [low, high] = bound;
    if (typeof low !== 'number' || typeof high !== 'number') return null;
    if (Number.isNaN(low) || Number.isNaN(high)) return null;
    if (value < low) return 'low';
    if (value > high) return 'high';
    return null;
  }

  if (typeof bound !== 'number' || Number.isNaN(bound)) return null;
  switch (operator) {
    case 'lt':
      return value >= bound ? 'high' : null;
    case 'lte':
      return value > bound ? 'high' : null;
    case 'gt':
      return value <= bound ? 'low' : null;
    case 'gte':
      return value < bound ? 'low' : null;
    default:
      return null;
  }
}

/**
 * Direction a value sits relative to its normal band: 'high', 'low', or null
 * (in range / not assessable). Prefers the band resolved for the patient (sex
 * and age at the study date); when none resolves it flags only when the male
 * and female bands agree on the direction. Returns null for an absent/zero
 * value (treated as empty), so an empty row never shows a range marker.
 */
export function getRangeDirection(
  referenceRanges: ReferenceRanges | null | undefined,
  sex: string | null | undefined,
  value: number | null | undefined,
  age?: number | null
): RangeDirection | null {
  if (value === null || value === undefined) return null;
  if (Number.isNaN(value) || value === 0) return null;

  const patientBand = resolveSeverityRanges(referenceRanges, sex, age);
  if (patientBand && typeof patientBand === 'object') {
    return bandDirection(patientBand.normal, value);
  }

  const male = bandDirection(
    resolveSeverityRanges(referenceRanges, 'male', age)?.normal,
    value
  );
  const female = bandDirection(
    resolveSeverityRanges(referenceRanges, 'female', age)?.normal,
    value
  );
  return male !== null && male === female ? male : null;
}
